import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { BackLinkComponent } from '../shared/components/back-link/back-link.component';
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { environment } from '../../environments/environment';
import { ResizableTextareaComponent } from '../shared/components/resizable-textarea/resizable-textarea.component';
import { MarkdownPipe } from '../markdown.pipe';
import { CatchTableComponent } from '../shared/components/catch-table/catch-table.component';
import { JobService } from '../services/job.service';
import * as mapboxgl from 'mapbox-gl';
import { Feature, FeatureCollection } from 'geojson';
import { TextFieldModule } from '@angular/cdk/text-field';

const DWELL_RADIUS_DEFAULT = 75;

interface SubZone {
  position: string;
  feature: string;
  depth: string;
  notes: string;
}

interface LocationAnalysis {
  named_location: string | null;
  confirmed_feature: string | null;
  material: string | null;
  depth_category: string | null;
  wind_exposure: string | null;
  tidal_influence: string | null;
  confidence_score_0_10: number | null;
  pattern_insight: string | null;
  sub_zones: SubZone[] | null;
  structure_elements: string[] | null;
}

interface DwellEvent {
  id: number;
  trip_id: number;
  start_time: string | null;
  end_time: string | null;
  duration_minutes: number;
  ping_count: number;
}

interface SpotDetail {
  id: number;
  name: string | null;
  location: string | null;
  center_lat: number;
  center_lng: number;
  radius_meters: number | null;
  notes: string | null;
  location_analysis: LocationAnalysis | null;
  conditions_analysis: string | null;
  conditions_confidence: number | null;
  visit_count: number;
  total_minutes: number;
  last_visited: string | null;
  catch_count: number;
  dwell_events: DwellEvent[];
}

@Component({
  selector: 'app-spot-detail',
  templateUrl: './spot-detail.component.html',
  styleUrls: ['./spot-detail.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, DatePipe, DecimalPipe, MatProgressSpinnerModule, MatButtonModule, MarkdownPipe, BackLinkComponent, LoadingSpinnerComponent, CatchTableComponent, TextFieldModule, ResizableTextareaComponent],
})
export class SpotDetailComponent implements OnInit, OnDestroy {
  spot: SpotDetail | null = null;
  isLoading = true;
  errorMessage: string | null = null;
  spotId!: number;

  // Satellite images
  tacticalImageUrl: SafeUrl | null = null;
  contextImageUrl: SafeUrl | null = null;
  private tacticalObjectUrl: string | null = null;
  private contextObjectUrl: string | null = null;
  lightboxUrl: SafeUrl | null = null;
  lightboxLabel = '';

  // Inline editing (name / location)
  editingField: string | null = null;
  editValue: any = null;

  // Editable radius
  radiusValue = DWELL_RADIUS_DEFAULT;
  isSavingRadius = false;

  // Notes
  notes = '';
  isSavingNotes = false;

  // Map edit
  isEditMode = false;
  isSavingLocation = false;

  // Analysis editing (same inline pattern as catch-detail)
  editingAnalysisField: string | null = null;
  editAnalysisValue: any = null;

  isBuildingMaps = false;
  isAnalyzingPhysical = false;
  isUpdatingConditions = false;
  isRebuildingConditions = false;
  private jobSub: Subscription | null = null;

  // Delete
  isDeleting = false;

  // Catches at this spot
  spotCatches: Feature[] = [];

  private map!: mapboxgl.Map;
  private marker: mapboxgl.Marker | null = null;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private jobService: JobService,
  ) {}

  ngOnInit(): void {
    this.spotId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadSpot();
    this.jobSub = this.jobService.getCompletions$('spot', this.spotId).subscribe(() => {
      this.loadSpot();
      this.loadMapImages();
    });
  }

  private loadSpot(): void {
    this.http.get<SpotDetail>(`${environment.apiUrl}/fishing_spots/${this.spotId}`).subscribe({
      next: (data) => {
        this.spot = data;
        this.notes = data.notes ?? '';
        this.radiusValue = data.radius_meters ?? DWELL_RADIUS_DEFAULT;
        this.isLoading = false;
        this.cdr.detectChanges();
        this.initMap();
        this.loadMapImages();
        this.loadSpotCatches();
      },
      error: () => {
        this.errorMessage = 'Failed to load spot.';
        this.isLoading = false;
      }
    });
  }

  private loadSpotCatches(): void {
    this.http.get<FeatureCollection>(`${environment.apiUrl}/fishing_spots/${this.spotId}/catches`).subscribe({
      next: (data) => {
        this.spotCatches = data.features ?? [];
        this.cdr.detectChanges();
        this.addCatchesLayer();
      },
      error: () => {}
    });
  }

  private addCatchesLayer(): void {
    if (!this.map) return;
    if (!this.map.loaded()) {
      this.map.once('load', () => this.addCatchesLayer());
      return;
    }
    const geojson: FeatureCollection = { type: 'FeatureCollection', features: this.spotCatches };
    if (this.map.getSource('spot-catches')) {
      (this.map.getSource('spot-catches') as mapboxgl.GeoJSONSource).setData(geojson);
    } else {
      this.map.addSource('spot-catches', { type: 'geojson', data: geojson });
      this.map.addLayer({
        id: 'spot-catches-layer',
        type: 'circle',
        source: 'spot-catches',
        paint: {
          'circle-radius': 6,
          'circle-color': ['get', 'fish_type_display_color'],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
        },
      });
      this.map.on('click', 'spot-catches-layer', (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const catchId = feature.id;
        if (!catchId) return;
        const ids = this.spotCatches.map((f: any) => f.id).filter(Boolean);
        this.router.navigate(['/catch', catchId], { queryParams: { list: ids.join(',') } });
      });
      this.map.on('mouseenter', 'spot-catches-layer', () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', 'spot-catches-layer', () => { this.map.getCanvas().style.cursor = ''; });
    }
  }

  navigateToCatch(feature: Feature): void {
    const catchId = (feature as any).id;
    if (!catchId) return;
    const ids = this.spotCatches.map((f: any) => f.id).filter(Boolean);
    this.router.navigate(['/catch', catchId], { queryParams: { list: ids.join(',') } });
  }

  // ── Map ──────────────────────────────────────────────────────────────────

  private initMap(): void {
    if (!this.spot) return;
    const { center_lat, center_lng } = this.spot;

    this.map = new mapboxgl.Map({
      accessToken: environment.mapbox.accessToken,
      container: 'spot-map',
      style: 'mapbox://styles/mapbox/standard-satellite',
      center: [center_lng, center_lat],
      zoom: 16,
      dragRotate: false,
      touchPitch: false
    });

    this.map.on('load', () => {
      this.addCircleLayer(center_lat, center_lng, this.radiusValue);
      this.marker = new mapboxgl.Marker({ color: '#ff4444', draggable: false })
        .setLngLat([center_lng, center_lat])
        .addTo(this.map);
    });
  }

  private buildCircleGeoJSON(lat: number, lng: number, radiusM: number): GeoJSON.Feature {
    const R = 6378137;
    const pts = 64;
    const coords: [number, number][] = [];
    for (let i = 0; i <= pts; i++) {
      const angle = (i * 360 / pts) * Math.PI / 180;
      const dLat = (radiusM / R) * (180 / Math.PI);
      const dLng = (radiusM / (R * Math.cos(Math.PI * lat / 180))) * (180 / Math.PI);
      coords.push([lng + dLng * Math.cos(angle), lat + dLat * Math.sin(angle)]);
    }
    return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } };
  }

  private addCircleLayer(lat: number, lng: number, radiusM: number): void {
    const geojson = this.buildCircleGeoJSON(lat, lng, radiusM);
    if (this.map.getSource('spot-radius')) {
      (this.map.getSource('spot-radius') as mapboxgl.GeoJSONSource).setData(geojson as any);
    } else {
      this.map.addSource('spot-radius', { type: 'geojson', data: geojson as any });
      this.map.addLayer({
        id: 'spot-radius-fill',
        type: 'fill',
        source: 'spot-radius',
        paint: { 'fill-color': '#ff4444', 'fill-opacity': 0.12 },
      });
      this.map.addLayer({
        id: 'spot-radius-outline',
        type: 'line',
        source: 'spot-radius',
        paint: { 'line-color': '#ff4444', 'line-width': 1.5, 'line-dasharray': [3, 3] },
      });
    }
  }

  startEditCenter(): void {
    if (!this.marker || !this.spot) return;
    this.marker.setDraggable(true);
    this.isEditMode = true;
    this.cdr.detectChanges();
  }

  cancelEditCenter(): void {
    if (!this.marker || !this.spot) return;
    this.marker.setDraggable(false);
    this.marker.setLngLat([this.spot.center_lng, this.spot.center_lat]);
    this.isEditMode = false;
    this.cdr.detectChanges();
  }

  saveCenter(): void {
    if (!this.marker || !this.spot) return;
    const lngLat = this.marker.getLngLat();
    this.isSavingLocation = true;
    this.http.patch(`${environment.apiUrl}/fishing_spots/${this.spotId}`, { center_lat: lngLat.lat, center_lng: lngLat.lng })
      .subscribe({
        next: (res: any) => {
          this.spot!.center_lat = res.center_lat;
          this.spot!.center_lng = res.center_lng;
          this.marker!.setDraggable(false);
          this.addCircleLayer(res.center_lat, res.center_lng, this.radiusValue);
          this.isEditMode = false;
          this.isSavingLocation = false;
          this.cdr.detectChanges();
        },
        error: () => { this.isSavingLocation = false; this.cdr.detectChanges(); }
      });
  }

  saveRadius(): void {
    this.isSavingRadius = true;
    this.http.patch(`${environment.apiUrl}/fishing_spots/${this.spotId}`, { radius_meters: this.radiusValue })
      .subscribe({
        next: (res: any) => {
          this.spot!.radius_meters = res.radius_meters;
          this.addCircleLayer(this.spot!.center_lat, this.spot!.center_lng, this.radiusValue);
          this.isSavingRadius = false;
          this.cdr.detectChanges();
        },
        error: () => { this.isSavingRadius = false; this.cdr.detectChanges(); }
      });
  }

  // ── Notes ─────────────────────────────────────────────────────────────────

  saveNotes(): void {
    this.isSavingNotes = true;
    this.http.patch(`${environment.apiUrl}/fishing_spots/${this.spotId}`, { notes: this.notes })
      .subscribe({
        next: () => { this.isSavingNotes = false; this.cdr.detectChanges(); },
        error: () => { this.isSavingNotes = false; this.cdr.detectChanges(); }
      });
  }

  // ── Inline edit (name / location) ─────────────────────────────────────────

  startEdit(field: string, currentValue: any): void {
    this.editingField = field;
    this.editValue = currentValue ?? '';
  }

  cancelEdit(): void {
    this.editingField = null;
    this.editValue = null;
  }

  saveEdit(field: string): void {
    this.http.patch<SpotDetail>(`${environment.apiUrl}/fishing_spots/${this.spotId}`, { [field]: this.editValue })
      .subscribe({
        next: (res) => {
          (this.spot as any)[field] = (res as any)[field];
          this.editingField = null;
          this.editValue = null;
          this.cdr.detectChanges();
        },
        error: () => { this.editingField = null; this.editValue = null; this.cdr.detectChanges(); }
      });
  }

  // ── Analysis inline edit ───────────────────────────────────────────────────

  startAnalysisEdit(field: string, currentValue: any): void {
    this.editingAnalysisField = field;
    this.editAnalysisValue = currentValue ?? '';
  }

  cancelAnalysisEdit(): void {
    this.editingAnalysisField = null;
    this.editAnalysisValue = null;
  }

  saveAnalysisEdit(field: string): void {
    const value = field === 'confidence_score_0_10' ? Number(this.editAnalysisValue) : this.editAnalysisValue;
    this.http.patch<{ location_analysis: LocationAnalysis }>(
      `${environment.apiUrl}/fishing_spots/${this.spotId}/analysis`,
      { [field]: value }
    ).subscribe({
      next: (res) => {
        this.spot!.location_analysis = res.location_analysis;
        this.editingAnalysisField = null;
        this.editAnalysisValue = null;
        this.cdr.detectChanges();
      },
      error: () => { this.editingAnalysisField = null; this.editAnalysisValue = null; this.cdr.detectChanges(); }
    });
  }

  // ── Satellite images ───────────────────────────────────────────────────────

  private loadMapImages(): void {
    for (const view of ['tactical', 'context'] as const) {
      this.http.get(`${environment.apiUrl}/fishing_spots/${this.spotId}/map/${view}`, { responseType: 'blob' })
        .subscribe({
          next: (blob) => {
            const objectUrl = URL.createObjectURL(blob);
            if (view === 'tactical') {
              if (this.tacticalObjectUrl) URL.revokeObjectURL(this.tacticalObjectUrl);
              this.tacticalObjectUrl = objectUrl;
              this.tacticalImageUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
            } else {
              if (this.contextObjectUrl) URL.revokeObjectURL(this.contextObjectUrl);
              this.contextObjectUrl = objectUrl;
              this.contextImageUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
            }
            this.cdr.detectChanges();
          },
          error: () => {}
        });
    }
  }

  openLightbox(url: SafeUrl, label: string): void {
    this.lightboxUrl = url;
    this.lightboxLabel = label;
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
    this.lightboxLabel = '';
  }

  // ── Maps + Physical Analysis ──────────────────────────────────────────────

  buildMaps(): void {
    if (this.isBuildingMaps) return;
    this.isBuildingMaps = true;
    this.cdr.detectChanges();
    this.http.post<any>(`${environment.apiUrl}/fishing_spots/${this.spotId}/analyze`, { maps_only: true }).subscribe({
      next: (res) => {
        if (res.job_id) this.jobService.register({ id: res.job_id, job_type: 'spot_analyze', entity_type: 'spot', entity_id: this.spotId, status: 'queued', message: 'Building maps...', created_at: new Date().toISOString(), completed_at: null });
        this.isBuildingMaps = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isBuildingMaps = false; this.cdr.detectChanges(); }
    });
  }

  analyzePhysical(): void {
    if (this.isAnalyzingPhysical) return;
    this.isAnalyzingPhysical = true;
    this.cdr.detectChanges();
    this.http.post<any>(`${environment.apiUrl}/fishing_spots/${this.spotId}/analyze`, {}).subscribe({
      next: (res) => {
        if (res.job_id) this.jobService.register({ id: res.job_id, job_type: 'spot_analyze', entity_type: 'spot', entity_id: this.spotId, status: 'queued', message: 'Analyzing physical characteristics...', created_at: new Date().toISOString(), completed_at: null });
        this.isAnalyzingPhysical = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isAnalyzingPhysical = false; this.cdr.detectChanges(); }
    });
  }

  // ── Conditions Analysis ───────────────────────────────────────────────────

  updateConditions(): void {
    if (this.isUpdatingConditions) return;
    this.isUpdatingConditions = true;
    this.cdr.detectChanges();
    this.http.post<any>(`${environment.apiUrl}/fishing_spots/${this.spotId}/conditions/update`, {}).subscribe({
      next: (res) => {
        if (res.job_id) this.jobService.register({ id: res.job_id, job_type: 'spot_conditions_update', entity_type: 'spot', entity_id: this.spotId, status: 'queued', message: 'Updating conditions analysis...', created_at: new Date().toISOString(), completed_at: null });
        this.isUpdatingConditions = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isUpdatingConditions = false; this.cdr.detectChanges(); }
    });
  }

  rebuildConditions(): void {
    if (this.isRebuildingConditions) return;
    this.isRebuildingConditions = true;
    this.cdr.detectChanges();
    this.http.post<any>(`${environment.apiUrl}/fishing_spots/${this.spotId}/conditions/rebuild`, {}).subscribe({
      next: (res) => {
        if (this.spot) this.spot.conditions_analysis = null;
        if (res.job_id) this.jobService.register({ id: res.job_id, job_type: 'spot_conditions', entity_type: 'spot', entity_id: this.spotId, status: 'queued', message: 'Rebuilding conditions analysis...', created_at: new Date().toISOString(), completed_at: null });
        this.isRebuildingConditions = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isRebuildingConditions = false; this.cdr.detectChanges(); }
    });
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  deleteSpot(): void {
    if (!confirm(`Delete Spot #${this.spotId}? This will also remove all dwell event history for this spot.`)) return;
    this.isDeleting = true;
    this.cdr.detectChanges();
    this.http.delete(`${environment.apiUrl}/fishing_spots/${this.spotId}`).subscribe({
      next: () => this.router.navigate(['/spots']),
      error: () => { this.isDeleting = false; this.cdr.detectChanges(); }
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  formatDuration(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  private revokeImageUrls(): void {
    if (this.tacticalObjectUrl) { URL.revokeObjectURL(this.tacticalObjectUrl); this.tacticalObjectUrl = null; }
    if (this.contextObjectUrl) { URL.revokeObjectURL(this.contextObjectUrl); this.contextObjectUrl = null; }
  }

  ngOnDestroy(): void {
    this.jobSub?.unsubscribe();
    this.revokeImageUrls();
    this.map?.remove();
  }
}
